import { CheckCircle, CalendarBlank, Clock, MapPin, CurrencyCircleDollar, Copy, HandDeposit } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { formatPrice, formatDateBR } from "./format";
import { useLocale } from "@/i18n";

export default function Confirmation({ booking, barber, onNewBooking }) {
  const { t, locale } = useLocale();
  const [copied, setCopied] = useState(false);

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(barber.deposit_pix_key);
      setCopied(true);
      toast.success(t("dep_copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("dep_copy_error"));
    }
  };

  return (
    <section className="animate-fade-up pt-6" data-testid="confirmation-screen">
      <div className="flex flex-col items-center text-center">
        <CheckCircle size={64} weight="fill" className="text-amber-500" />
        <h2 className="text-3xl font-black tracking-tight mt-4">{t("booked_title")}</h2>
        <p className="text-sm text-zinc-400 mt-2">
          {booking.client_name.split(" ")[0]}, {t("booked_sub")}
        </p>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500 mt-6">
          {t("code_label")}
        </p>
        <p className="font-display text-4xl font-bold text-amber-500 tracking-wider mt-1" data-testid="booking-code">
          {booking.code}
        </p>
        <p className="text-xs text-zinc-500 mt-2">{t("code_note")}</p>
      </div>

      <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3" data-testid="confirmation-summary">
        <p className="font-bold text-lg text-zinc-100">{booking.service_name}</p>
        <p className="flex items-center gap-2 text-sm text-zinc-400">
          <CalendarBlank size={16} className="text-amber-500" weight="duotone" />
          <span>{formatDateBR(booking.date, locale)}</span> {t("at")} {booking.time}
        </p>
        <p className="flex items-center gap-2 text-sm text-zinc-400">
          <Clock size={16} className="text-amber-500" weight="duotone" />
          {t("duration_pre")} {booking.duration_min} min {t("with_word")} {booking.professional_name || barber.name}
        </p>
        {barber.address && (
          <p className="flex items-center gap-2 text-sm text-zinc-400">
            <MapPin size={16} className="text-amber-500" weight="duotone" />
            {barber.address}
          </p>
        )}
        <div className="border-t border-zinc-800 pt-3 flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-zinc-400">
            <CurrencyCircleDollar size={16} className="text-amber-500" weight="duotone" />
            {t("pay_at_shop")}
          </span>
          <span className="text-lg font-bold text-amber-500" data-testid="confirmation-price">
            {formatPrice(booking.price, locale)}
          </span>
        </div>
      </div>

      {barber.deposit_enabled && barber.deposit_amount > 0 && barber.deposit_pix_key && (
        <div className="mt-4 bg-amber-500/10 border border-amber-500/40 rounded-xl p-4" data-testid="deposit-card">
          <p className="flex items-center gap-2 text-sm font-bold text-amber-400">
            <HandDeposit size={18} weight="duotone" /> {t("dep_card_title")}
          </p>
          <p className="text-xl font-black text-zinc-50 mt-2" data-testid="deposit-amount">{formatPrice(barber.deposit_amount, locale)}</p>
          <div className="mt-2 flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2">
            <span className="flex-1 text-sm text-zinc-200 truncate font-mono" data-testid="deposit-key">{barber.deposit_pix_key}</span>
            <button data-testid="deposit-copy-btn" onClick={copyKey} className="shrink-0 flex items-center gap-1 text-xs font-bold text-amber-500 hover:text-amber-400 transition-colors">
              <Copy size={14} weight="bold" /> {copied ? t("dep_copied") : t("dep_copy")}
            </button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">{t("dep_card_hint")}</p>
        </div>
      )}

      <button
        data-testid="new-booking-btn"
        onClick={onNewBooking}
        className="mt-6 w-full h-12 rounded-lg border border-zinc-800 text-zinc-300 font-semibold hover:border-amber-500/60 hover:text-amber-500 transition-colors active:scale-95"
      >
        {t("new_booking")}
      </button>
    </section>
  );
}
